import { Activity, useEffect, useState } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { PulseLoader } from 'react-spinners';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Link,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LinkIcon from '@mui/icons-material/Link';
import { useLocation } from 'react-router-dom';
import { usePresignedDownload, ResourceType } from '../../hooks/usePreSignedDownload';

import { AdvisorMode as AdvisorModeIcon, Close, Avatar } from '@brainpool/icon-pack';

import { useSendMessage } from '../../hooks/useSendMessage';
import { useListChats } from '../..//hooks/useListChats';
import { useGetChatMessages } from '../../hooks/useGetChatMessages';
import { extractUserMessage, parseAdvisorJsonContent } from './functions';
import { UserRole } from '../../types/User';

export interface AdvisorModeProps {
  onClose: () => void;
  caseId: string | null | undefined;
  userRole: UserRole; // currently not used, but might be useful in the future
}
interface ParsedChat {
  advisor_json: any;
  user_message?: string;
  created_date: string;
  role?: string;
}
export function AdvisorMode({ onClose, caseId, userRole }: AdvisorModeProps) {
  const theme = useTheme();

  const { sendMessage, loading: sendingMessage } = useSendMessage();
  const { fetchChats, data: chatList, loading: chatListLoading } = useListChats();
  const { getChatMessages, data: chats, loading: chatLoading } = useGetChatMessages();

  const [parsedChats, setParsedChats] = useState<ParsedChat[]>([]);
  // Track pending user messages
  const [pendingMessages, setPendingMessages] = useState<ParsedChat[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [useExternalData, setUseExternalData] = useState(false);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const {
    getPresignedDownloadUrl,
    loading: presignedDataLoading,
    data: presignedData,
  } = usePresignedDownload();

  if (!caseId) {
    return null;
  }

  useEffect(() => {
    if (presignedData?.getPreSignedDownloadUrl?.url) {
      window.open(presignedData.getPreSignedDownloadUrl.url, '_blank', 'noopener,noreferrer');
    }
  }, [presignedData]);

  useEffect(() => {
    if (caseId) {
      fetchChats(caseId);
    }
  }, [caseId]);

  // get chat from cache
  const chatId =
    (chatList?.listChats?.items ?? []).find(chat => chat && chat.name === 'AdvisorChat')?.id ||
    undefined;

  useEffect(() => {
    if (chatId && caseId) {
      getChatMessages(caseId, chatId);
    }
  }, [chatId, caseId]);

  // get chat from cache
  const chat = chats?.getChatMessages?.items || [];

  useEffect(() => {
    if (chat.length > 0) {
      // first 2 messages are prompts
      // Filter out system-generated USER messages
      const newParsedChats: ParsedChat[] = chat
        .slice(2)
        .filter(
          msg => !(msg.role === 'USER' && msg.created_by === 'User_system:tunedd-analysis-handler')
        )
        .map(msg => {
          let user_message: string | undefined;
          if (msg.role === 'USER') {
            user_message = extractUserMessage(msg.content);
          }

          return {
            advisor_json: parseAdvisorJsonContent(msg.content),
            user_message,
            role: msg.role,
            created_date: msg.created_date,
          };
        });
      // Remove any pending messages that now exist in backend (by user_message match)
      setPendingMessages(pending => {
        const filtered = pending.filter(pendingMsg => {
          const matched = newParsedChats.some(chatMsg => {
            if (chatMsg.role !== 'USER') return false;
            if (chatMsg.user_message !== pendingMsg.user_message) return false;
            const dt = Math.abs(
              new Date(chatMsg.created_date).getTime() - new Date(pendingMsg.created_date).getTime()
            );
            return dt < 60000;
          });
          return !matched;
        });
        return filtered;
      });
      setParsedChats(newParsedChats);
    }
  }, [chat]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setInputValue(event.target.value);
  };

  const handleSendMessage = async (content: string) => {
    if (chatId && caseId) {
      // append optimistic message to pending
      setPendingMessages(prev => [
        ...prev,
        {
          advisor_json: {},
          user_message: content,
          created_date: new Date().toISOString(),
          role: 'USER',
          use_google_search: useExternalData,
        },
      ]);

      try {
        await sendMessage({ content, chatId, caseId, use_google_search: useExternalData });
      } catch (error) {
        // Remove the optimistic message if send failed
        setPendingMessages(prev => prev.filter(msg => msg.user_message !== content));
      }
    }
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (
      event.key === 'Enter' &&
      !event.shiftKey &&
      // Only send if the event target is a textarea (for multiline)
      (event.target as HTMLElement).tagName === 'TEXTAREA'
    ) {
      event.preventDefault();
      if (inputValue.trim()) {
        handleSendMessage(inputValue);
        setInputValue('');
      }
    }
  };

  return (
    <Grid className="advisor-mode" gap={2} data-testid="advisor-mode">
      <Grid className="advisor-mode-chat">
        <Box sx={{ display: 'flex', justifyContent: 'end' }}>
          <Button sx={{ width: 48, height: 48, minWidth: 48 }} onClick={onClose}>
            <Close fill="white" />
          </Button>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            p: 4,
            textAlign: 'center',
            alignItems: 'center',
          }}
        >
          <AdvisorModeIcon size={32} fill={theme.palette.info.dark} />
          <Typography variant="h2" color="info.dark">
            Advisor Mode
          </Typography>
        </Box>
        {chatId && (
          <Box>
            {[...parsedChats, ...pendingMessages].map((msg, index) => (
              <Box
                key={`${msg.role ?? 'unknown'}-${msg.created_date}-${msg.user_message ?? ''}`}
                sx={{ mb: 2, display: 'flex' }}
              >
                {msg.role === 'USER' ? (
                  msg.user_message || msg.advisor_json ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 2,
                        ml: 'auto',
                        maxWidth: '80%',
                        alignItems: 'flex-start',
                        alignSelf: 'flex-end',
                      }}
                    >
                      <Avatar size={24} fill={theme.palette.info.main} />
                      <Box sx={{ flex: 1 }}>
                        <Typography>
                          {msg.user_message ? msg.user_message : msg.advisor_json}
                        </Typography>{' '}
                        <Typography color="info.dark" sx={{ mt: 1 }}>
                          {new Date(msg.created_date).toUTCString()}
                        </Typography>
                      </Box>
                    </Box>
                  ) : null
                ) : msg.advisor_json ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', maxWidth: '100%', flex: 1 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        backgroundColor: theme.palette.grey[600],
                        border: `1px solid ${theme.palette.info.dark}`,
                        p: 2,
                      }}
                    >
                      {msg.advisor_json.text && index !== 0 && (
                        <Typography>{msg.advisor_json.text}</Typography>
                      )}
                      {typeof msg.advisor_json === 'string' && index !== 0 && (
                        <Markdown>{msg.advisor_json}</Markdown>
                      )}

                      {typeof msg.advisor_json === 'object' && msg.advisor_json.response && (
                        <Box sx={{ mb: 2 }}>
                          {(() => {
                            try {
                              const parsed = JSON.parse(msg.advisor_json.response);
                              // Check for content first, then text
                              if (parsed.content) {
                                return (
                                  <Markdown remarkPlugins={[remarkGfm]}>{parsed.content}</Markdown>
                                );
                              }
                              if (parsed.text) {
                                return (
                                  <Markdown remarkPlugins={[remarkGfm]}>{parsed.text}</Markdown>
                                );
                              }
                              return (
                                <Markdown remarkPlugins={[remarkGfm]}>
                                  {msg.advisor_json.response}
                                </Markdown>
                              );
                            } catch {
                              return (
                                <Markdown remarkPlugins={[remarkGfm]}>
                                  {msg.advisor_json.response}
                                </Markdown>
                              );
                            }
                          })()}
                        </Box>
                      )}
                      {(msg.advisor_json.references?.length ?? 0) > 0 &&
                        (() => {
                          const refs = msg.advisor_json.references ?? [];
                          const points: Array<
                            { type: 'doc'; ref: any } | { type: 'web'; urls: string[] }
                          > = [];
                          refs.forEach((ref: any) => {
                            if (ref.source === 'documents') {
                              points.push({ type: 'doc', ref });
                            } else if (ref.source === 'web' && ref.urls?.length > 0) {
                              const last = points[points.length - 1];
                              if (last?.type === 'web') {
                                last.urls.push(...ref.urls);
                              } else {
                                points.push({ type: 'web', urls: [...ref.urls] });
                              }
                            }
                          });
                          const totalCount = points.length;
                          return (
                            <>
                              <Accordion
                                disableGutters
                                elevation={0}
                                sx={{
                                  '&:before': { display: 'none' },
                                  border: 1,
                                  borderColor: theme.palette.grey[700],
                                  borderRadius: 1,
                                  overflow: 'hidden',
                                  backgroundColor: theme.palette.grey[800],
                                  '&:first-of-type': { borderRadius: 1 },
                                }}
                              >
                                <AccordionSummary
                                  expandIcon={
                                    <ExpandMoreIcon sx={{ color: theme.palette.grey[300] }} />
                                  }
                                  sx={{
                                    backgroundColor: theme.palette.grey[800],
                                    minHeight: 40,
                                    color: theme.palette.grey[100],
                                    '& .MuiAccordionSummary-content': { my: 1 },
                                    '&:hover': {
                                      backgroundColor: theme.palette.grey[700],
                                    },
                                  }}
                                >
                                  <Typography variant="subtitle2" sx={{ color: 'white' }}>
                                    References {totalCount > 0 && `(${totalCount})`}
                                  </Typography>
                                </AccordionSummary>
                                <AccordionDetails
                                  sx={{
                                    pt: 0,
                                    backgroundColor: theme.palette.grey[900],
                                    color: 'white',
                                  }}
                                >
                                  {points.map((point, index) =>
                                    point.type === 'doc' ? (
                                      <Typography
                                        key={`ref-${index}`}
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          py: 0.5,
                                        }}
                                      >
                                        {index + 1}. {point.ref.filename} Page: {point.ref.page}{' '}
                                        <LinkIcon
                                          sx={{
                                            color: theme.palette.primary.light,
                                            ml: 1,
                                            cursor: 'pointer',
                                          }}
                                          fontSize="small"
                                          onClick={() =>
                                            getPresignedDownloadUrl(
                                              point.ref.doc_id,
                                              caseId || '',
                                              ResourceType.DOCUMENT
                                            )
                                          }
                                        />
                                      </Typography>
                                    ) : (
                                      <Typography key={`ref-${index}`} sx={{ py: 0.5 }}>
                                        {index + 1}. Web references (see below)
                                      </Typography>
                                    )
                                  )}
                                  {(() => {
                                    const allWebUrls = points
                                      .filter(
                                        (p): p is { type: 'web'; urls: string[] } =>
                                          p.type === 'web'
                                      )
                                      .flatMap(p => p.urls);
                                    if (allWebUrls.length === 0) return null;
                                    return (
                                      <Box
                                        sx={{
                                          mt: 2,
                                          pt: 2,
                                          borderTop: 1,
                                          borderColor: theme.palette.grey[700],
                                        }}
                                      >
                                        <Typography
                                          variant="subtitle2"
                                          sx={{ mb: 1, color: 'white' }}
                                        >
                                          Web URLs
                                        </Typography>
                                        <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                          {allWebUrls.map((url: string, urlIndex: number) => (
                                            <Typography
                                              key={urlIndex}
                                              component="li"
                                              sx={{
                                                display: 'list-item',
                                                py: 0.25,
                                                color: theme.palette.grey[200],
                                              }}
                                            >
                                              <Link
                                                href={url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                underline="hover"
                                                sx={{
                                                  color: theme.palette.primary.light,
                                                  '&:hover': {
                                                    color: theme.palette.primary.main,
                                                  },
                                                }}
                                              >
                                                {url}
                                              </Link>
                                            </Typography>
                                          ))}
                                        </Box>
                                      </Box>
                                    );
                                  })()}
                                </AccordionDetails>
                              </Accordion>
                              {msg.advisor_json.google_search_grounding_rendered_content && (
                                <Box
                                  display="none"
                                  component="div"
                                  sx={{ mt: 1 }}
                                  // Google ADK grounded search returns HTML (including <style> tags)
                                  // that must be rendered as-is for compliance.
                                  dangerouslySetInnerHTML={{
                                    __html:
                                      msg.advisor_json.google_search_grounding_rendered_content,
                                  }}
                                ></Box>
                              )}
                            </>
                          );
                        })()}
                      {msg.advisor_json.questions?.map((question: SuggestionTileProps) => (
                        <Button
                          key={question.title}
                          variant="text"
                          onClick={() => handleSendMessage(question.question)}
                          disabled={userRole == 'Reader'}
                        >
                          <SuggestionTile {...question} />
                        </Button>
                      ))}
                    </Box>
                    <Typography color="info.dark" sx={{ mt: 1 }}>
                      {new Date(msg.created_date).toUTCString()}
                    </Typography>
                  </Box>
                ) : null}
              </Box>
            ))}
          </Box>
        )}

        {(chatListLoading || chatLoading || sendingMessage) && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <PulseLoader
              loading={chatListLoading || chatLoading || sendingMessage}
              size={12}
              color={theme.palette.primary.main}
            />
          </Box>
        )}
      </Grid>

      <Activity mode={userRole === 'Owner' || userRole === 'Editor' ? 'visible' : 'hidden'}>
        <Box sx={{ p: 2, pt: 0 }}>
          <TextField
            data-testid="advisor-mode-input"
            multiline
            rows={4}
            placeholder={'Ask about any information in this data room...'}
            fullWidth
            slotProps={{
              input: { className: 'advisor-mode-textfield' },
            }}
            sx={{
              backgroundColor: theme.palette.grey[600],
            }}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            disabled={sendingMessage || chatLoading || chatListLoading}
          />
        </Box>
      </Activity>
    </Grid>
  );
}

interface SuggestionTileProps {
  title: string;
  question: string;
}

function SuggestionTile({ title, question }: SuggestionTileProps) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        textAlign: 'left',
        backgroundColor: theme.palette.grey[600],
        border: `2px solid ${theme.palette.primary.main}`,
        p: 2,
        width: '100%',
      }}
    >
      <Typography variant="h4" color="info.main">
        {title}
      </Typography>
      <Typography variant="h6" color="info.main">
        {question}
      </Typography>
    </Box>
  );
}

export default AdvisorMode;
